import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY, patchAppUser } from "./appAuthSlice";
import { isPendingRoundActive } from "../hooks/usePendingPlayCountdown";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

const adjustCoinBalance = (dispatch, getState, delta) => {
  const current = Number(getState?.()?.appAuth?.user?.COIN || 0);
  if (!Number.isFinite(current)) return;
  dispatch(patchAppUser({ COIN: Math.max(0, current + delta) }));
};

export const fetchBondNumbersConstants = createAsyncThunk(
  "bondNumbers/fetchConstants",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/constants");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load Bond Numbers rules"));
    }
  }
);

export const fetchBondNumbersLanding = createAsyncThunk(
  "bondNumbers/fetchLanding",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/landing");
      return res.data?.pendingRound || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load pending ticket"));
    }
  }
);

export const fetchBondNumbersPool = createAsyncThunk(
  "bondNumbers/fetchPool",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/pool");
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investor pool"));
    }
  }
);

export const fetchBondNumbersHistory = createAsyncThunk(
  "bondNumbers/fetchHistory",
  async ({ limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/rounds/history", { params: { limit } });
      return res.data?.rounds || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load numbers history"));
    }
  }
);

export const fetchBondNumbersInvestors = createAsyncThunk(
  "bondNumbers/fetchInvestors",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/investors", { params: { limit } });
      return res.data?.investors || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load investors"));
    }
  }
);

export const fetchBondNumbersPlayers = createAsyncThunk(
  "bondNumbers/fetchPlayers",
  async ({ limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/bond-numbers/players", { params: { limit } });
      return res.data?.players || [];
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Failed to load players"));
    }
  }
);

export const bondNumbersReserve = createAsyncThunk(
  "bondNumbers/reserve",
  async ({ stake }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/reserve", { stake });
      const round = res.data?.round || null;
      if (round?.stake) adjustCoinBalance(dispatch, getState, -Number(round.stake));
      dispatch(fetchBondNumbersPool());
      dispatch(fetchBondNumbersInvestors({ limit: 100 }));
      dispatch(fetchBondNumbersPlayers({ limit: 100 }));
      return round;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not reserve ticket"));
    }
  }
);

export const bondNumbersReveal = createAsyncThunk(
  "bondNumbers/reveal",
  async ({ roundId, playerPick }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/reveal", { roundId, playerPick });
      const round = res.data?.round || null;
      if (round?.status === "Won") {
        const net = Number(round.playerNetPayout || 0);
        if (net > 0) adjustCoinBalance(dispatch, getState, net);
      }
      dispatch(fetchBondNumbersPool());
      dispatch(fetchBondNumbersInvestors({ limit: 100 }));
      dispatch(fetchBondNumbersPlayers({ limit: 100 }));
      return round;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not reveal number"));
    }
  }
);

export const bondNumbersCancel = createAsyncThunk(
  "bondNumbers/cancel",
  async ({ roundId, stake = 0 }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/cancel", { roundId });
      if (stake > 0) adjustCoinBalance(dispatch, getState, Number(stake));
      dispatch(fetchBondNumbersPool());
      dispatch(fetchBondNumbersInvestors({ limit: 100 }));
      dispatch(fetchBondNumbersPlayers({ limit: 100 }));
      return res.data?.round || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel ticket"));
    }
  }
);

export const bondNumbersInvest = createAsyncThunk(
  "bondNumbers/invest",
  async ({ amount }, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/invest", { amount });
      const invested = Number(res.data?.amount || 0);
      if (invested > 0) adjustCoinBalance(dispatch, getState, -invested);
      dispatch(fetchBondNumbersPool());
      dispatch(fetchBondNumbersInvestors({ limit: 100 }));
      dispatch(fetchBondNumbersPlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Invest failed"));
    }
  }
);

export const bondNumbersInvestorCashout = createAsyncThunk(
  "bondNumbers/investorCashout",
  async ({ amount } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/investor/cashout", { amount });
      const withdrawn = Number(res.data?.amount || 0);
      if (withdrawn > 0) adjustCoinBalance(dispatch, getState, withdrawn);
      dispatch(fetchBondNumbersPool());
      dispatch(fetchBondNumbersInvestors({ limit: 100 }));
      dispatch(fetchBondNumbersPlayers({ limit: 100 }));
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Cash out failed"));
    }
  }
);

export const bondNumbersInvestorToggleActive = createAsyncThunk(
  "bondNumbers/investorToggleActive",
  async ({ isActive }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/bond-numbers/investor/toggle-active", { isActive });
      return res.data || null;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not update investor status"));
    }
  }
);

const initialState = {
  constants: null,
  pendingRound: null,
  lastSettledRound: null,
  pool: null,
  players: [],
  investors: [],
  history: [],
  loading: false,
  loadingPlayers: false,
  loadingInvestors: false,
  actionLoading: false,
  investorActionLoading: false,
  error: null,
};

const bondNumbersSlice = createSlice({
  name: "bondNumbers",
  initialState,
  reducers: {
    setBondNumbersPendingRound: (state, { payload }) => {
      state.pendingRound = payload || null;
    },
    setBondNumbersLastSettled: (state, { payload }) => {
      state.lastSettledRound = payload || null;
    },
    clearBondNumbersPendingRound: (state) => {
      state.pendingRound = null;
    },
    clearBondNumbersLastSettled: (state) => {
      state.lastSettledRound = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBondNumbersConstants.fulfilled, (state, { payload }) => {
        state.constants = payload;
      })
      .addCase(fetchBondNumbersLanding.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBondNumbersLanding.fulfilled, (state, { payload }) => {
        state.loading = false;
        const ttlMs = state.constants?.pendingPlayTtlMs || 120 * 1000;
        state.pendingRound =
          payload?.status === "Pending" && isPendingRoundActive(payload, ttlMs) ? payload : null;
      })
      .addCase(fetchBondNumbersLanding.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchBondNumbersPool.fulfilled, (state, { payload }) => {
        state.pool = payload;
      })
      .addCase(fetchBondNumbersPlayers.pending, (state) => {
        state.loadingPlayers = true;
      })
      .addCase(fetchBondNumbersPlayers.fulfilled, (state, { payload }) => {
        state.players = payload || [];
        state.loadingPlayers = false;
      })
      .addCase(fetchBondNumbersPlayers.rejected, (state) => {
        state.loadingPlayers = false;
      })
      .addCase(fetchBondNumbersInvestors.pending, (state) => {
        state.loadingInvestors = true;
      })
      .addCase(fetchBondNumbersInvestors.fulfilled, (state, { payload }) => {
        state.investors = payload || [];
        state.loadingInvestors = false;
      })
      .addCase(fetchBondNumbersInvestors.rejected, (state) => {
        state.loadingInvestors = false;
      })
      .addCase(bondNumbersReserve.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondNumbersReserve.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = payload;
      })
      .addCase(bondNumbersReserve.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondNumbersReveal.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondNumbersReveal.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        state.pendingRound = null;
        state.lastSettledRound = payload;
      })
      .addCase(bondNumbersReveal.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondNumbersCancel.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(bondNumbersCancel.fulfilled, (state) => {
        state.actionLoading = false;
        state.pendingRound = null;
      })
      .addCase(bondNumbersCancel.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(bondNumbersInvest.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondNumbersInvest.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondNumbersInvest.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondNumbersInvestorCashout.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondNumbersInvestorCashout.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondNumbersInvestorCashout.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondNumbersInvestorToggleActive.pending, (state) => {
        state.investorActionLoading = true;
      })
      .addCase(bondNumbersInvestorToggleActive.fulfilled, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(bondNumbersInvestorToggleActive.rejected, (state) => {
        state.investorActionLoading = false;
      })
      .addCase(fetchBondNumbersHistory.fulfilled, (state, { payload }) => {
        state.history = payload || [];
      });
  },
});

export const {
  setBondNumbersPendingRound,
  setBondNumbersLastSettled,
  clearBondNumbersPendingRound,
  clearBondNumbersLastSettled,
} = bondNumbersSlice.actions;

export const selectBondNumbersConstants = (state) => state.bondNumbers?.constants;
export const selectBondNumbersPendingRound = (state) => state.bondNumbers?.pendingRound;
export const selectBondNumbersLastSettled = (state) => state.bondNumbers?.lastSettledRound;
export const selectBondNumbersPool = (state) => state.bondNumbers?.pool;
export const selectBondNumbersPlayers = (state) => state.bondNumbers?.players || [];
export const selectBondNumbersInvestors = (state) => state.bondNumbers?.investors || [];
export const selectBondNumbersLoadingPlayers = (state) => state.bondNumbers?.loadingPlayers;
export const selectBondNumbersLoadingInvestors = (state) => state.bondNumbers?.loadingInvestors;
export const selectBondNumbersActionLoading = (state) => state.bondNumbers?.actionLoading;
export const selectBondNumbersInvestorActionLoading = (state) => state.bondNumbers?.investorActionLoading;

export default bondNumbersSlice.reducer;
